/* ───────────────────────────────────────────────────────────────────────────
   O16 sticky buy bar — dock the REAL buy buttons on scroll-past
   ─────────────────────────────────────────────────────────────────────────────
   When the in-flow buy buttons (.product__buy_buttons — the quantity selector +
   Add to cart + Buy with Shop) scroll fully out of view, we pin that very element
   to the bottom of the viewport as a sticky bar, and release it when it scrolls
   back into view.

   Why pin the real element instead of a separate reconstructed bar:
   • The <product-form>, its variant state, and the dynamic-checkout button stay
     intact (no clone, no DOM move) — Add to cart and Buy with Shop keep working.
   • Our .product-section-scoped styling (green Add-to-cart, slim height) still
     applies, because the element keeps its place in the DOM tree.

   The PDP renders twice (a desktop wrapper + a mobile wrapper); CSS hides one per
   breakpoint. The bar is MOBILE ONLY (owner 2026-06-22): we wire up only the
   instance inside .main-product-mobile-section-wrapper, so the computer layout
   never gets a sticky bar. A visibility guard still skips that instance while the
   mobile wrapper itself is hidden (desktop viewport), and resize re-checks it so a
   breakpoint change can never leave a phantom bar behind.

   Mechanics (flicker-free):
   • A zero-height SENTINEL sits just AFTER the buttons. We observe it; the moment
     its top crosses above the viewport top, the buttons have fully scrolled past,
     so we dock. Because the sentinel is never resized, its geometry is stable.
   • A separate SPACER sits just BEFORE the buttons. It is 0 high normally and is
     grown to the buttons' height only while docked, so the page never jumps when
     the buttons leave the flow. The spacer is not observed, so resizing it can
     never feed back into the dock decision.

   Styling lives in o16-product-gallery.css
   (.o16-buybar--docked / .o16-buybar-spacer / .o16-buybar-sentinel).
─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  function setup(buyButtons) {
    if (!buyButtons) return;
    // No sticky buy bar on desktop (owner 2026-06-22): only the mobile-wrapper
    // instance is ever wired up. The desktop-wrapper instance is left alone, so
    // the bar can never dock on the computer layout regardless of scroll.
    if (!buyButtons.closest('.main-product-mobile-section-wrapper')) return;
    if (buyButtons.dataset.o16Buybar) return;
    buyButtons.dataset.o16Buybar = '1';

    // ── O16 buy-bar info-line sync (mobile docked bar only) ──────────────────
    // Keep VARIANT + PRICE in sync on variant:change. We do NOT read the page
    // price element: id="price-{section}" is duplicated across the desktop +
    // mobile wrappers and the base code updates only the first (desktop) one.
    // Format from the event's cents-integer price via the global formatMoney().
    var variantEl = buyButtons.querySelector('.js-o16-buybar-variant');
    var priceEl = buyButtons.querySelector('.js-o16-buybar-price');
    if (variantEl || priceEl) {
      document.addEventListener('variant:change', function (e) {
        var v = e && e.detail && e.detail.variant;
        if (!v) return;
        if (variantEl) variantEl.textContent = v.title || '';
        if (priceEl && typeof formatMoney === 'function') {
          var o16fmt = (window.theme && window.theme.moneyFormat) || '';
          var o16html = formatMoney(v.price, o16fmt);
          if (v.compare_at_price && v.compare_at_price > v.price) {
            var o16pct = Math.floor((v.compare_at_price - v.price) * 100 / v.compare_at_price);
            o16html += ' <s class="o16-buybar-info__compare">' + formatMoney(v.compare_at_price, o16fmt) + '</s>' +
                       ' <span class="o16-buybar-info__savings">−' + o16pct + '%</span>';
          }
          priceEl.innerHTML = o16html;
        }
      });
    }

    var parent = buyButtons.parentNode;
    if (!parent) return;

    // Spacer (before): holds the vacated space while docked. Not observed.
    var spacer = document.createElement('div');
    spacer.className = 'o16-buybar-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    parent.insertBefore(spacer, buyButtons);

    // Sentinel (after): zero-height trigger. Observed.
    var sentinel = document.createElement('div');
    sentinel.className = 'o16-buybar-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    parent.insertBefore(sentinel, buyButtons.nextSibling);

    var docked = false;

    function isVisible() {
      // offsetParent is null when an ancestor is display:none (the inactive
      // breakpoint instance); a zero width means it is not laid out.
      return (
        buyButtons.offsetParent !== null &&
        buyButtons.getBoundingClientRect().width > 0
      );
    }

    function setDocked(on) {
      if (on === docked) return;
      docked = on;
      // Read the height while the element is still in flow, then pin it.
      spacer.style.height = on ? buyButtons.offsetHeight + 'px' : '';
      buyButtons.classList.toggle('o16-buybar--docked', on);
    }

    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (!isVisible()) {
          setDocked(false);
          return;
        }
        // Dock once the buttons' bottom edge (the sentinel) has scrolled above
        // the viewport top — i.e. they are fully out of view.
        var scrolledPast =
          !entry.isIntersecting && entry.boundingClientRect.top <= 0;
        setDocked(scrolledPast);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    // A breakpoint change can hide the instance that is currently docked.
    window.addEventListener('resize', function () {
      if (!isVisible()) setDocked(false);
    });
  }

  function init() {
    document
      .querySelectorAll('.product__buy_buttons')
      .forEach(setup);
  }

  // Exposed so anything that replaces the buy row can wire the new one up.
  // setup() already refuses a second pass on the same element
  // (dataset.o16Buybar), so calling this again is safe and cheap — it only
  // ever picks up buy rows that have never been seen before. Needed because
  // the bundle ladder's page takeover swaps this section's markup wholesale,
  // and a bar that self-initialised once on DOMContentLoaded would otherwise
  // be dead on the new markup — proven on DEV: data-o16-buybar was simply
  // absent after a swap, so the mobile primary CTA could never dock again.
  window.O16StickyBuybar = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
